/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import {
  tableV2MultilineGridSx,
  tableV2StackedCellContentSx,
} from '../../../components/v2/tableV2Styles';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import * as actionsVeiculos from '../../../store/veiculo/actions';
import * as Roles from '../../../constants/role';
import * as selectorsAuth from '../../../store/auth/reducer';
import Formulario from './Formulario';
import { formatNumber, getTipoVeiculoLabel } from './helpers';

const defaultFilter = {
  ativo: true,
  finalidades: null,
};

const getVeiculoIdFromSearch = (search) => {
  if (!search) return null;
  const id = new URLSearchParams(search).get('id');
  return id && String(id).trim() ? String(id).trim() : null;
};

const LabeledValue = ({ label, value }) => (
  <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
    <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
      {label}
    </Box>
    {value ?? '—'}
  </Typography>
);

LabeledValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const VeiculosV2 = ({ authData, exportVeiculo, history, location }) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [openExport, setOpenExport] = useState(false);
  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const isAdmin = () =>
    authData && (authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN);

  useEffect(() => {
    const deepLinkId = getVeiculoIdFromSearch(location?.search);
    if (!deepLinkId) return;
    setId(deepLinkId);
    setOpen(true);
  }, [location?.search]);

  const onEdit = (rowId) => {
    setOpen(true);
    setId(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
  };

  const clearDeepLinkId = () => {
    if (!getVeiculoIdFromSearch(location?.search)) return;
    const params = new URLSearchParams(location.search);
    params.delete('id');
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const onClose = (isSave) => {
    if (isSave) {
      setReload((current) => !current);
    }
    setOpen(false);
    setId(null);
    clearDeepLinkId();
  };

  const headerActions = [];
  if (isAdmin()) {
    headerActions.push({
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    });
    headerActions.push({
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    });
  }

  const columns = [
    {
      field: 'placa',
      headerName: 'Placa',
      minWidth: 140,
      flex: 0.7,
      renderCell: (params) => (
        <Box sx={tableV2StackedCellContentSx}>
          <LabeledValue label="Placa:" value={params.row.placa} />
          <LabeledValue label="Num. Frota:" value={params.row.numFrota} />
        </Box>
      ),
    },
    {
      field: 'modelo',
      headerName: 'Categoria & Modelo',
      minWidth: 220,
      flex: 1.2,
      renderCell: (params) => (
        <Box sx={tableV2StackedCellContentSx}>
          <LabeledValue label="Modelo:" value={params.row.modelo} />
          <LabeledValue label="Categoria:" value={params.row.categoriaNome} />
          <LabeledValue label="Finalidade:" value={params.row.finalidadeValue} />
        </Box>
      ),
    },
    {
      field: 'tipoVeiculo',
      headerName: 'Proprietário',
      minWidth: 110,
      flex: 0.5,
      valueGetter: (_, row) => getTipoVeiculoLabel(row.tipoVeiculo),
    },
    {
      field: 'kmAtual',
      headerName: 'Quilômetro',
      minWidth: 150,
      flex: 0.7,
      renderCell: (params) => (
        <Box sx={tableV2StackedCellContentSx}>
          <LabeledValue label="Inicial:" value={formatNumber(params.row.kmInicial)} />
          <LabeledValue label="Atual:" value={formatNumber(params.row.kmAtual)} />
        </Box>
      ),
    },
    {
      field: 'ativo',
      headerName: 'Ativo',
      flex: 0.3,
      valueGetter: (_, row) => (row.ativo === true ? 'Sim' : 'Não'),
    },
    { field: 'ultimoRepasse', headerName: 'Último Repasse', flex: 0.5 },
    { field: 'regiaoUltimoRepasse', headerName: 'Região', flex: 0.5 },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Veículos</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="veiculos/filtro"
          paginationUrl="/v2/veiculos"
          filterData={localFilter}
          refreshKey={reload}
          columns={columns}
          getRowHeight={() => 'auto'}
          gridSx={tableV2MultilineGridSx}
          onEdit={isAdmin() ? onEdit : undefined}
          headerActions={headerActions}
          pageSize={10}
          sortComparator={(a, b) => (a.placa || '').localeCompare(b.placa || '')}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          fields={[
            { field: 'useCategoriaVeiculo', md: 4, xs: 12 },
            { field: 'useVeiculo', md: 4, xs: 12 },
            { field: 'useTipoVeiculo', md: 4, xs: 12 },
            { field: 'useFinalidadeVeiculo', md: 4, xs: 12 },
            { field: 'useAtivo', md: 4, xs: 12 },
            { field: 'useGaragem', md: 4, xs: 12 },
            { field: 'useCodigo', codigoLabel: 'Modelo', md: 4, xs: 12 },
            { field: 'useColaborador', md: 4, xs: 12 },
            { field: 'usePossuiRastreador', md: 4, xs: 12 },
            { field: 'useNumRastreador', md: 4, xs: 12 },
            { field: 'usePossuiCamera', md: 4, xs: 12 },
            { field: 'useNumCamera', md: 4, xs: 12 },
            { field: 'useNumFrota', md: 4, xs: 12 },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario id={id} open={open} onClose={onClose} />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportVeiculo(localFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>
            Deseja baixar todas as informações filtradas?
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

VeiculosV2.propTypes = {
  exportVeiculo: PropTypes.func.isRequired,
  authData: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  exportVeiculo: actionsVeiculos.exportVeiculo,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(VeiculosV2));
